import * as React from 'react';
import clsx from 'clsx';
import type { Echo } from '@/types';
import { getTheme } from '../themes';
import { defaultTheme } from '../themes/default.theme';
import dechoesLogo from '@/assets/images/dechoes-logo-transparent.png';
import waveElementChat from '@/assets/images/svg/wave_element_chat.svg';
import algolympBg from '@/assets/images/elements/algolymp_background.webp';

import { PublicChatHeader } from './PublicChatHeader';
import { PublicMessageList } from './PublicMessageList';
import { PublicChatInput } from './PublicChatInput';
import BackgroundBubbles from '@/components/common/BackgroundBubbles';
import { usePublicChat } from '../hooks/usePublicChat';
import { SuggestedQuestions } from '@/features/my-echo/components/suggested-questions/SuggestedQuestions';
import { findMessageWithSuggestions } from '@/features/my-echo/components/suggested-questions/utils';

// Public echo configurations
const PUBLIC_ECHO_CONFIG: Record<string, { id: string; name: string; theme: string }> = {
  algolymp: {
    id: 'f70ee590-c6aa-42f2-97a2-1bd100ebd56b',
    name: 'Vlad Munteanu',
    theme: 'first',
  },
};

/**
 * Detect which public echo to use based on subdomain.
 * e.g., algolymp.dechoes.com -> 'algolymp'
 */
function detectPublicEchoFromSubdomain(): string | null {
  if (typeof window === 'undefined') return null;

  const hostname = window.location.hostname;

  // Check for known subdomains
  for (const key of Object.keys(PUBLIC_ECHO_CONFIG)) {
    if (hostname.startsWith(`${key}.`) || hostname === key) {
      return key;
    }
  }

  // Also check for localhost development with query param
  const urlParams = new URLSearchParams(window.location.search);
  const publicEcho = urlParams.get('public_echo');
  if (publicEcho && PUBLIC_ECHO_CONFIG[publicEcho]) {
    return publicEcho;
  }

  return null;
}

interface PublicChatViewProps {
  echo: Echo;
  echoName: string;
  pageSlug?: string;
}

export const PublicChatView: React.FC<PublicChatViewProps> = ({
  echo: propEcho,
  echoName: propEchoName,
  pageSlug,
}) => {
  // Detect if this is a known public subdomain. Secret page slugs must use
  // their backend-assigned echo, even when hosted on a public subdomain.
  const publicEchoKey = pageSlug ? null : detectPublicEchoFromSubdomain();
  const publicConfig = publicEchoKey ? PUBLIC_ECHO_CONFIG[publicEchoKey] : null;

  // Use detected config or fall back to props
  const echoId = pageSlug ? propEcho.id : publicConfig?.id || propEcho.id;
  const echoName = publicConfig?.name || propEchoName;

  // Custom public pages use the same purple stage theme as staging.
  const envTheme = import.meta.env['VITE_PUBLIC_CHAT_THEME'];
  const themeName = pageSlug ? 'first' : publicConfig?.theme || envTheme || 'default';

  const theme = React.useMemo(() => getTheme(themeName), [themeName]);

  const chatTheme = React.useMemo(
    () => ({
      ...theme,
      bubbles: defaultTheme.bubbles,
      input: defaultTheme.input,
    }),
    [theme]
  );

  // TTS state - default to elevenlabs for public chat
  const [ttsEnabled] = React.useState(true);
  const ttsProvider = ttsEnabled ? 'elevenlabs' : null;

  // Use real public chat hook
  const {
    messages,
    isLoading,
    error,
    sendMessage,
    sendAudioMessage,
    echoInfo,
    isEchoLoading,
    stopAudio,
    currentlyPlayingId,
    isAudioLoading,
  } = usePublicChat({
    echoId,
    echoName,
    ttsProvider,
    ...(pageSlug ? { pageSlug } : {}),
  });

  const handleSend = (text: string) => {
    if (text.trim()) {
      sendMessage(text);
    }
  };

  const handleSendAudio = (blob: Blob, waveform: number[], duration: number) => {
    sendAudioMessage(blob, waveform, duration);
  };

  const lastAiMessage = findMessageWithSuggestions(messages);
  const suggestedQuestions = lastAiMessage?.suggested_questions ?? [];

  const handleQuestionClick = (questionText: string) => {
    handleSend(questionText);
  };

  // Construct echo object for header (use fetched echoInfo if available)
  const displayEcho: Echo = {
    ...propEcho,
    id: echoInfo?.id || echoId,
    name: echoInfo?.name || echoName,
    imageUrl: echoInfo?.profile_image_url || propEcho.imageUrl,
    category: echoInfo?.category || propEcho.category,
  };

  return (
    <div
      className={clsx(
        'flex h-dvh w-full flex-col items-center justify-center p-0',
        theme.container.background
      )}
    >
      <div className='flex size-full flex-col'>
        <div className='flex w-full shrink-0 items-center justify-end px-6 pb-2 pt-4 md:pr-[83px]'>
          <img
            src={dechoesLogo}
            alt='D.ECHO'
            className='h-[36px] w-auto object-contain md:h-[82.84px]'
          />
        </div>

        {/* Chat Container (Card) */}
        <div
          className={clsx(
            'relative flex w-full overflow-hidden',
            'flex-col md:flex-row',
            theme.container.wrapper,
            theme.container.backdrop,
            '!m-0 !h-auto min-h-0 flex-1'
          )}
        >
          {theme.name === 'default' && (
            <div className='pointer-events-none absolute inset-0 z-0 select-none overflow-hidden'>
              <div className='opacity-100 transition-opacity duration-200'>
                <BackgroundBubbles count={20} />
              </div>

              {/* Bottom Spinning Wave */}
              <div
                className={clsx(
                  'absolute bottom-0 left-1/2 h-auto w-full md:w-[70%]',
                  'opacity-80',
                  '-translate-x-1/2 translate-y-[60%]'
                )}
              >
                <img src={waveElementChat} alt='' className='size-full animate-spin-slow-120' />
              </div>
            </div>
          )}

          {/* LEFT PANE: Avatar & Info */}
          <div className='relative w-full shrink-0 md:h-full md:flex-1 md:pl-[47px]'>
            {isEchoLoading ? (
              <div className='flex h-[250px] w-full items-center justify-center md:h-full'>
                <div className='size-10 animate-spin rounded-full border-4 border-white/20 border-t-white' />
              </div>
            ) : (
              <PublicChatHeader
                echo={displayEcho}
                theme={theme}
                useLegacyAlgolympBranding={!!publicConfig}
              />
            )}
          </div>

          {/* RIGHT PANE: Chat Interface */}
          <div className='relative z-20 flex min-h-0 flex-1 flex-col overflow-hidden md:h-full md:w-[888px]'>
            {/*
               Gradient Overlay (Top-down)
               - Mobile Only (md:hidden)
            */}
            {theme.name === 'crimson' && (
              <div
                className='pointer-events-none absolute left-0 top-0 z-30 h-[60px] w-full md:hidden'
                style={{
                  background:
                    'linear-gradient(180deg, #4750A3 19.88%, rgba(71, 80, 163, 0.2) 48.98%, rgba(71, 80, 163, 0) 66.3%)',
                }}
              />
            )}

            {/* ADDED: Algolymp Background (Bottom Anchored) - Desktop Only */}
            {theme.name === 'crimson' && (
              <img
                src={algolympBg}
                alt=''
                className='pointer-events-none absolute bottom-0 left-0 z-0 hidden w-full object-contain opacity-100 md:block'
              />
            )}

            {/* Error display */}
            {error && (
              <div className='mx-4 my-2 rounded-lg bg-red-500/20 p-3 text-sm text-red-300'>
                {error}
              </div>
            )}

            <main className='scrollbar-dark relative z-20 flex-1 overflow-y-auto px-4 py-2'>
              <PublicMessageList
                messages={messages}
                isLoading={isLoading}
                theme={chatTheme}
                onStopAudio={stopAudio}
                currentlyPlayingId={currentlyPlayingId}
                isAudioLoading={isAudioLoading}
              />
            </main>

            <div className='relative z-20 flex w-full shrink-0 flex-col items-center justify-center'>
              <div className='w-full max-w-[805px]'>
                {/* Suggested Questions Area */}
                {suggestedQuestions.length > 0 && (
                  <div className='px-4 pb-2'>
                    <SuggestedQuestions
                      questions={suggestedQuestions}
                      onQuestionSelect={handleQuestionClick}
                      disabled={isLoading}
                    />
                  </div>
                )}

                <PublicChatInput
                  onSend={handleSend}
                  onSendAudio={handleSendAudio}
                  isLoading={isLoading}
                  theme={chatTheme}
                />
              </div>
            </div>
          </div>

          {/* ADDED: Global Bottom Gradient Overlay */}
          <div
            className={clsx(
              'pointer-events-none absolute bottom-0 left-0 w-full',
              'z-10', // Sits above avatar, below text/chat
              'h-[30%]', // Height of the void
              theme.name === 'crimson' && 'bg-crimson-bottom-overlay'
            )}
            style={
              theme.name === 'crimson'
                ? undefined
                : {
                    background: 'linear-gradient(to top, #110C1D 45%, rgba(17, 12, 29, 0) 100%)',
                  }
            }
          />
        </div>
      </div>
    </div>
  );
};
